import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useForm } from 'react-hook-form';
import { toast } from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { Mail, ArrowLeft, RefreshCw } from 'lucide-react';

const TwoFactorAuth = () => {
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [countdown, setCountdown] = useState(0);
  const navigate = useNavigate();
  const { pendingEmail, verify2FA, verifyEmail, sendVerificationCode } = useAuth();

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm();

  // Countdown timer for resend button
  useEffect(() => {
    if (countdown > 0) {
      const timer = setTimeout(() => setCountdown(countdown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [countdown]);

  // Redirect if no pending email
  useEffect(() => {
    if (!pendingEmail) {
      navigate('/login');
    }
  }, [pendingEmail, navigate]);

  const onSubmit = async (data) => {
    setLoading(true);
    try {
      const result = await verify2FA(pendingEmail, data.code);
      if (result.success) {
        toast.success(result.message);
        navigate('/dashboard');
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('An error occurred during verification');
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    setResendLoading(true);
    try {
      const result = await sendVerificationCode(pendingEmail);
      if (result.success) {
        toast.success('Verification code sent successfully');
        setCountdown(60); // 60 second cooldown
      } else {
        toast.error(result.message);
      }
    } catch (error) {
      toast.error('Failed to resend verification code');
    } finally {
      setResendLoading(false);
    }
  };

  const handleBackToLogin = () => {
    navigate('/login');
  };

  if (!pendingEmail) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:py-6 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <button
            onClick={handleBackToLogin}
            className="flex items-center text-blue-600 hover:text-blue-500 mb-4"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </button>
          
          <div className="text-center">
            <div className="mx-auto flex items-center justify-center h-12 w-12 rounded-full bg-blue-100">
              <Mail className="h-6 w-6 text-blue-600" />
            </div>
            <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
              Verify your email
            </h2>
            <p className="mt-2 text-center text-sm text-gray-600">
              We've sent a verification code to{' '}
              <span className="font-medium text-gray-900">{pendingEmail}</span>
            </p>
          </div>
        </div>
        
        <form className="mt-8 space-y-6" onSubmit={handleSubmit(onSubmit)}>
          <div>
            <label htmlFor="code" className="form-label">
              Verification Code
            </label>
            <input
              id="code"
              type="text"
              autoComplete="one-time-code"
              className={`form-input text-center text-2xl tracking-widest ${
                errors.code ? 'border-red-500' : ''
              }`}
              placeholder="XXXXXX"
              maxLength="6"
              {...register('code', {
                required: 'Verification code is required',
                pattern: {
                  value: /^\d{6}$/,
                  message: 'Please enter a 6-digit code',
                },
              })}
            />
            {errors.code && (
              <p className="mt-1 text-sm text-red-600">{errors.code.message}</p>
            )}
          </div>

          <div className="space-y-4">
            <button
              type="submit"
              disabled={loading}
              className="btn-primary w-full flex justify-center items-center"
            >
              {loading ? (
                <div className="spinner mr-2"></div>
              ) : null}
              {loading ? 'Verifying...' : 'Verify Code'}
            </button>

            <div className="text-center">
              <p className="text-sm text-gray-600">
                Didn't receive the code?
              </p>
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendLoading || countdown > 0}
                className="text-blue-600 hover:text-blue-500 text-sm font-medium disabled:text-gray-400 disabled:cursor-not-allowed flex items-center justify-center w-full mt-2"
              >
                {resendLoading ? (
                  <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                ) : null}
                {countdown > 0
                  ? `Resend in ${countdown}s`
                  : resendLoading
                  ? 'Sending...'
                  : 'Resend code'}
              </button>
            </div>
          </div>
        </form>

        <div className="text-center">
          <p className="text-xs text-gray-500">
            Check your email inbox and spam folder for the verification code.
            The code will expire in 10 minutes.
          </p>
        </div>
      </div>
    </div>
  );
};

export default TwoFactorAuth; 